import { createContext, useContext, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle, X, Sparkles, FileText } from "lucide-react";

const BackgroundNoteGenCtx = createContext(null);

export const useBackgroundNoteGen = () => useContext(BackgroundNoteGenCtx);

/**
 * Manages AI note generation jobs that survive React navigation.
 * Jobs use SSE streaming with polling fallback — all detached from component lifecycle.
 */
export function BackgroundNoteGenProvider({ children, api }) {
  const [jobs, setJobs] = useState([]);
  const completedNotesRef = useRef({}); // { [localJobId]: noteData }

  const updateJob = useCallback((id, patch) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));
  }, []);

  const removeJob = useCallback((id) => {
    setJobs(prev => prev.filter(j => j.id !== id));
    delete completedNotesRef.current[id];
  }, []);

  /** Pick up a completed note (consumed once by the NoteScriber when it re-mounts). */
  const consumeCompletedNote = useCallback((localJobId) => {
    const data = completedNotesRef.current[localJobId];
    if (data) delete completedNotesRef.current[localJobId];
    return data;
  }, []);

  /**
   * Submit a note generation job to run in the background.
   * @param {string} backendJobId — the job_id returned from POST /notes/generate
   * @param {Object} opts — { patientName, onComplete }
   */
  const submitNoteGen = useCallback((backendJobId, opts = {}) => {
    const { patientName = "Note", onComplete } = opts;
    const localId = `ng_${Date.now()}`;

    setJobs(prev => [...prev, {
      id: localId,
      backendJobId,
      patientName,
      status: "generating",
      progress: "Generating note...",
    }]);

    // Run SSE + polling detached from component lifecycle
    (async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const token = localStorage.getItem("session_token") || "";
        const sseUrl = `${backendUrl}/api/notes/generate-stream/${backendJobId}?token=${encodeURIComponent(token)}`;

        // Try SSE first
        const sseResult = await new Promise((resolve) => {
          let resolved = false;
          const done = (result) => { if (!resolved) { resolved = true; resolve(result); } };

          try {
            const es = new EventSource(sseUrl);
            let gotFirst = false;

            es.onmessage = (event) => {
              try {
                const msg = JSON.parse(event.data);
                if (!gotFirst) { gotFirst = true; }

                if (msg.type === "progress") {
                  updateJob(localId, { progress: msg.progress?.message || "Processing..." });
                } else if (msg.type === "section") {
                  updateJob(localId, { progress: `Writing section ${(msg.index || 0) + 1}...` });
                } else if (msg.type === "complete") {
                  es.close();
                  done({ success: true, data: msg.data });
                } else if (msg.type === "error") {
                  es.close();
                  done({ success: false, error: msg.detail || "Generation failed" });
                }
              } catch {}
            };

            es.onerror = () => {
              es.close();
              if (!gotFirst) {
                done(null); // SSE never connected → fall back to polling
              } else {
                done(null); // SSE dropped → fall back to polling
              }
            };

            setTimeout(() => { if (!gotFirst) { es.close(); done(null); } }, 12000);
          } catch {
            done(null);
          }
        });

        if (sseResult?.success) {
          completedNotesRef.current[localId] = sseResult.data;
          updateJob(localId, { status: "done", progress: "Note ready" });
          if (onComplete) onComplete(sseResult.data);
          toast.success(`Note generated for ${patientName}`, { duration: 6000 });
          setTimeout(() => removeJob(localId), 15000);
          return;
        }
        if (sseResult && !sseResult.success) {
          updateJob(localId, { status: "error", progress: sseResult.error });
          toast.error(`Note failed for ${patientName}: ${sseResult.error}`);
          setTimeout(() => removeJob(localId), 10000);
          return;
        }

        // Polling fallback
        updateJob(localId, { progress: "Generating note (polling)..." });
        const pollStart = Date.now();
        while (Date.now() - pollStart < 300000) {
          await new Promise(r => setTimeout(r, 3000));
          let pollRes;
          try {
            pollRes = await api.get(`/notes/generate-status/${backendJobId}`, { timeout: 15000 });
          } catch (e) {
            if (e.code === "ECONNABORTED" || e.code === "ERR_NETWORK") continue;
            throw e;
          }
          const { status: jStatus, data: noteResult, detail: jError, progress } = pollRes.data;
          if (progress?.message) updateJob(localId, { progress: progress.message });

          if (jStatus === "complete" && noteResult) {
            completedNotesRef.current[localId] = noteResult;
            updateJob(localId, { status: "done", progress: "Note ready" });
            if (onComplete) onComplete(noteResult);
            toast.success(`Note generated for ${patientName}`, { duration: 6000 });
            setTimeout(() => removeJob(localId), 15000);
            return;
          }
          if (jStatus === "error") {
            const errMsg = jError || "Generation failed";
            updateJob(localId, { status: "error", progress: errMsg });
            if (errMsg.toLowerCase().includes("budget")) {
              toast.error("AI budget may be low. Check Profile > Universal Key > Add Balance.", { duration: 8000 });
            } else {
              toast.error(`Note failed: ${errMsg}`, { duration: 6000 });
            }
            setTimeout(() => removeJob(localId), 10000);
            return;
          }
        }
        updateJob(localId, { status: "error", progress: "Timed out" });
        toast.error("Note generation timed out.");
        setTimeout(() => removeJob(localId), 10000);
      } catch (err) {
        updateJob(localId, { status: "error", progress: err.message || "Failed" });
        toast.error(`Note generation error: ${err.message}`);
        setTimeout(() => removeJob(localId), 10000);
      }
    })();

    return localId;
  }, [api, updateJob, removeJob]);

  const hasActiveJobs = jobs.some(j => j.status === "generating");

  return (
    <BackgroundNoteGenCtx.Provider value={{ submitNoteGen, jobs, hasActiveJobs, removeJob, consumeCompletedNote }}>
      {children}
      {jobs.length > 0 && <NoteGenFloater jobs={jobs} onDismiss={removeJob} />}
    </BackgroundNoteGenCtx.Provider>
  );
}

function NoteGenFloater({ jobs, onDismiss }) {
  const [minimized, setMinimized] = useState(false);

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-forest text-white shadow-lg hover:bg-forest-dark transition-colors"
        data-testid="bg-notegen-minimized"
      >
        <Sparkles className="w-4 h-4 animate-pulse" />
        <span className="text-xs font-medium">{jobs.filter(j => j.status === "generating").length} generating</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden" data-testid="bg-notegen-floater">
      <div className="flex items-center justify-between px-3 py-2 bg-forest text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="text-xs font-semibold">Background Note Generation</span>
        </div>
        <button onClick={() => setMinimized(true)} className="text-white/60 hover:text-white text-xs">
          minimize
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
        {jobs.map(job => (
          <div key={job.id} className="flex items-center gap-2.5 px-3 py-2.5" data-testid={`bg-notegen-job-${job.id}`}>
            {job.status === "done" ? (
              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            ) : job.status === "error" ? (
              <X className="w-4 h-4 text-red-500 flex-shrink-0" />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin text-forest flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-700 truncate flex items-center gap-1">
                <FileText className="w-3 h-3" /> {job.patientName}
              </p>
              <p className="text-[10px] text-slate-400 truncate">{job.progress}</p>
            </div>
            {(job.status === "done" || job.status === "error") && (
              <button onClick={() => onDismiss(job.id)} className="text-slate-300 hover:text-slate-500">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
